import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  X,
  Building2,
  User,
  Mail,
  Phone,
  MapPin,
  FileText,
  Calendar,
  Wrench,
  Edit,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  AlertTriangle,
  Loader2,
  Droplet,
  History,
  ShieldCheck
} from 'lucide-react';
import { clientsAPI, serviceCallsAPI } from '@shared/apiClient';
import { FlagIcon } from '@shared/flagIcons';
import { formatDate, formatCoolingType } from '@shared/utils';
import toast from 'react-hot-toast';
import { ServiceCallEditModal } from '../pages/ServiceCalls';
import CopyTextButton from './CopyTextButton';

const StatusBadge = ({ status, resolution }) => {
  if (resolution === 'skipped') {
    return (
      <span className="px-2 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-800">
        Skipped
      </span>
    );
  }
  if (resolution === 'bumped') {
    return (
      <span className="px-2 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-800">
        Bumped
      </span>
    );
  }

  const statusConfig = {
    assigned: { color: 'bg-blue-100 text-blue-800', label: 'Assigned' },
    scheduled: { color: 'bg-yellow-100 text-yellow-800', label: 'Scheduled' },
    in_progress: { color: 'bg-purple-100 text-purple-800', label: 'In Progress' },
    completed: { color: 'bg-green-100 text-green-800', label: 'Completed' },
    cancelled: { color: 'bg-red-100 text-red-800', label: 'Cancelled' },
    rescheduled: { color: 'bg-orange-100 text-orange-800', label: 'Rescheduled' }
  };

  const config = statusConfig[status] || statusConfig.assigned;
  
  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full ${config.color}`}>
      {config.label}
    </span>
  );
};

const PriorityBadge = ({ priority }) => {
  const priorityConfig = {
    low: { color: 'bg-gray-100 text-gray-800', label: 'Low' },
    medium: { color: 'bg-blue-100 text-blue-800', label: 'Medium' },
    high: { color: 'bg-orange-100 text-orange-800', label: 'High' },
    urgent: { color: 'bg-red-100 text-red-800', label: 'Urgent' }
  };

  const config = priorityConfig[priority] || priorityConfig.medium;
  
  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full ${config.color}`}>
      {config.label}
    </span>
  );
};

// Mirrors the rule vocabulary in ClientFormModal; hard rules read red/green,
// soft rules read neutral so a dispatcher can tell enforcement from preference.
const TECH_RULE_DISPLAY = {
  required: { label: 'Only', className: 'border-emerald-200 bg-emerald-50 text-emerald-700' },
  preferred: { label: 'Prefer', className: 'border-blue-200 bg-blue-50 text-blue-700' },
  avoid: { label: 'Avoid', className: 'border-amber-200 bg-amber-50 text-amber-700' },
  never: { label: 'Never', className: 'border-red-200 bg-red-50 text-red-700' }
};

export default function ClientDetailsModal({ client, isOpen, onClose, onEdit }) {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('details'); // 'details', 'history'
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [serviceCallToEdit, setServiceCallToEdit] = useState(null);

  // Fetch full client details
  const { data: clientData, isLoading: isLoadingClient } = useQuery(
    ['client', client?.id],
    () => clientsAPI.getClient(client.id),
    { enabled: isOpen && !!client }
  );

  // Fetch service history
  const { data: serviceHistoryData, isLoading: isLoadingHistory } = useQuery(
    ['serviceHistory', client?.id],
    () => serviceCallsAPI.getServiceCalls({ clientId: client.id, limit: 1000 }),
    { enabled: isOpen && !!client }
  );

  // Update oil change month mutation with optimistic updates
  const updateOilChangeMonthMutation = useMutation(
    (month) => clientsAPI.updateClient(client.id, { oilChangeMonth: month }),
    {
      onMutate: async (month) => {
        // Cancel any outgoing refetches to avoid overwriting optimistic update
        await queryClient.cancelQueries(['client', client.id]);
        await queryClient.cancelQueries('clients');
        await queryClient.cancelQueries('allClients');

        // Snapshot the previous value
        const previousClient = queryClient.getQueryData(['client', client.id]);
        const previousClients = queryClient.getQueryData('clients');
        const previousAllClients = queryClient.getQueryData('allClients');

        // Optimistically update the cache
        queryClient.setQueryData(['client', client.id], (old) => {
          if (!old) return old;
          const clientData = old.client || old.data?.client || old;
          return {
            ...old,
            client: { ...clientData, oilChangeMonth: month },
            data: old.data ? {
              ...old.data,
              client: { ...old.data.client, oilChangeMonth: month }
            } : undefined
          };
        });

        queryClient.setQueryData('clients', (old) => {
          if (!old) return old;
          const data = old.clients || old.data?.clients || [];
          return {
            ...old,
            clients: data.map(c => 
              c.id === client.id ? { ...c, oilChangeMonth: month } : c
            ),
            data: old.data ? {
              ...old.data,
              clients: (old.data.clients || []).map(c => 
                c.id === client.id ? { ...c, oilChangeMonth: month } : c
              )
            } : undefined
          };
        });

        queryClient.setQueryData('allClients', (old) => {
          if (!old) return old;
          return {
            ...old,
            clients: (old.clients || []).map(c => 
              c.id === client.id ? { ...c, oilChangeMonth: month } : c
            )
          };
        });

        return { previousClient, previousClients, previousAllClients };
      },
      onError: (error, month, context) => {
        // Rollback on error
        if (context?.previousClient) {
          queryClient.setQueryData(['client', client.id], context.previousClient);
        }
        if (context?.previousClients) {
          queryClient.setQueryData('clients', context.previousClients);
        }
        if (context?.previousAllClients) {
          queryClient.setQueryData('allClients', context.previousAllClients);
        }
        toast.error(error.response?.data?.message || 'Failed to update oil change month');
      },
      onSuccess: () => {
        toast.success('Oil change month updated successfully');
      },
      onSettled: () => {
        // Always refetch after error or success to ensure consistency
        queryClient.invalidateQueries(['client', client.id]);
        queryClient.invalidateQueries('clients');
        queryClient.invalidateQueries('allClients');
      }
    }
  );

  const fullClient = clientData?.data?.client || client;

  const queueForRepairMutation = useMutation(
    (notes) => clientsAPI.queueContractClient({ clientId: fullClient.id, notes }),
    {
      onSuccess: () => {
        toast.success('Added to the repair queue');
        queryClient.invalidateQueries('schedulingBacklog');
        queryClient.invalidateQueries('queuedServiceCalls');
        queryClient.invalidateQueries('queuedServiceCallCount');
        queryClient.invalidateQueries(['serviceHistory', fullClient.id]);
        queryClient.invalidateQueries('clients');
        queryClient.invalidateQueries('allClients');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to add to repair queue');
      }
    }
  );

  const handleQueueForRepair = () => {
    const notes = window.prompt('Add a note for the repair queue (optional):', '');
    if (notes === null) return;
    queueForRepairMutation.mutate(notes.trim());
  };

  const handleMonthClick = (month) => {
    // If clicking the same month that's already the oil change month, do nothing
    if (fullClient.oilChangeMonth === month) {
      return;
    }
    
    // Update the oil change month
    updateOilChangeMonthMutation.mutate(month);
  };
  const serviceCalls = serviceHistoryData?.data?.serviceCalls || [];
  const serviceCallIds = serviceCalls.map((serviceCall) => serviceCall.id);
  const { data: serviceHistoryDetailsData, isLoading: isLoadingHistoryDetails } = useQuery(
    ['serviceHistoryDetails', client?.id, serviceCallIds],
    async () => {
      const responses = await Promise.all(
        serviceCallIds.map((id) => serviceCallsAPI.getServiceCall(id))
      );
      return responses
        .map((response) => response.data?.serviceCall)
        .filter(Boolean);
    },
    {
      enabled: isOpen && !!client && activeTab === 'history' && serviceCallIds.length > 0
    }
  );
  const fullServiceCalls = serviceHistoryDetailsData?.length ? serviceHistoryDetailsData : serviceCalls;

  if (!isOpen || !client) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        />

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          {/* Header */}
          <div className="bg-gray-50 px-4 py-5 sm:px-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Building2 className="h-6 w-6 text-blue-600" />
                <div>
                  <h3 className="text-lg font-medium text-gray-900">
                    {fullClient.isCommercial && fullClient.companyName 
                      ? fullClient.companyName 
                      : `${fullClient.contactFirstName} ${fullClient.contactLastName}`}
                  </h3>
                  <p className="text-sm text-gray-500">
                    Customer Details
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {onEdit && (
                  <button
                    onClick={() => {
                      onEdit(fullClient);
                    }}
                    className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                    title="Edit Customer"
                  >
                    <Edit className="h-5 w-5" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              <button
                onClick={() => setActiveTab('details')}
                className={`py-4 px-6 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'details'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Details
              </button>
              <button
                onClick={() => setActiveTab('history')}
                className={`py-4 px-6 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'history'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Service History ({serviceCalls.length})
              </button>
            </nav>
          </div>

          {/* Content */}
          <div className="px-4 py-5 sm:p-6 max-h-[70vh] overflow-y-auto">
            {isLoadingClient && activeTab === 'details' ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              </div>
            ) : activeTab === 'details' ? (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Left Column: Contact Information */}
                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                      <User className="h-4 w-4 mr-2 text-blue-600" />
                      Contact Information
                    </h4>
                    <dl className="space-y-2">
                      <div>
                        <dt className="text-xs font-medium text-gray-500">Name</dt>
                        <dd className="mt-0.5 text-sm text-gray-900">
                          {fullClient.contactFirstName} {fullClient.contactLastName}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs font-medium text-gray-500">Email</dt>
                        <dd className="mt-0.5 text-sm text-gray-900">
                          {fullClient.email || <span className="text-gray-400">Not set</span>}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs font-medium text-gray-500">Phone</dt>
                        <dd className="mt-0.5 text-sm text-gray-900">
                          {fullClient.phone || <span className="text-gray-400">Not set</span>}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs font-medium text-gray-500">Secondary Phone</dt>
                        <dd className="mt-0.5 text-sm text-gray-900">
                          {fullClient.secondPhone || <span className="text-gray-400">Not set</span>}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs font-medium text-gray-500">Status</dt>
                        <dd className="mt-0.5">
                          {fullClient.isActive ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Active
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              Inactive
                            </span>
                          )}
                        </dd>
                      </div>
                    </dl>
                  </div>

                  {/* Address */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                      <MapPin className="h-4 w-4 mr-2 text-blue-600" />
                      Location
                    </h4>
                    <dl className="space-y-2">
                      <div>
                        <dt className="text-xs font-medium text-gray-500">Address</dt>
                        <dd className="mt-0.5 text-sm text-gray-900">{fullClient.address}</dd>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <dt className="text-xs font-medium text-gray-500">City</dt>
                          <dd className="mt-0.5 text-sm text-gray-900">{fullClient.city}</dd>
                        </div>
                        <div>
                          <dt className="text-xs font-medium text-gray-500">State</dt>
                          <dd className="mt-0.5 text-sm text-gray-900">{fullClient.state}</dd>
                        </div>
                      </div>
                      <div>
                        <dt className="text-xs font-medium text-gray-500">Zip Code</dt>
                        <dd className="mt-0.5 text-sm text-gray-900">{fullClient.zipCode || <span className="text-gray-400">Not set</span>}</dd>
                      </div>
                      <div>
                        <dt className="text-xs font-medium text-gray-500">Sales tax</dt>
                        <dd className="mt-0.5 text-sm text-gray-900">
                          {fullClient.taxExempt ? (
                            <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">Tax exempt</span>
                          ) : (
                            <span className="text-gray-600">Taxable (state rules apply)</span>
                          )}
                        </dd>
                      </div>
                    </dl>
                  </div>

                  {/* Contract Information */}
                  {fullClient.isContractClient && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                        <Calendar className="h-4 w-4 mr-2 text-blue-600" />
                        Contract Details
                      </h4>
                      <dl className="space-y-2">
                        <div>
                          <dt className="text-xs font-medium text-gray-500">Service Months</dt>
                          <dd className="mt-1">
                            {fullClient.serviceCycleMonths && fullClient.serviceCycleMonths.length > 0 ? (
                              <div className="flex items-center space-x-2">
                                {fullClient.serviceCycleMonths.map((month, index) => {
                                  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                                  const isOilChangeMonth = fullClient.oilChangeMonth === month;
                                  const isUpdating = updateOilChangeMonthMutation.isLoading;
                                  
                                  return (
                                    <button
                                      key={index}
                                      onClick={() => handleMonthClick(month)}
                                      disabled={isUpdating}
                                      className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium transition-all ${
                                        isOilChangeMonth
                                          ? 'bg-amber-100 text-amber-800 border-2 border-amber-300 cursor-default'
                                          : 'bg-gray-100 text-gray-700 border-2 border-transparent hover:bg-gray-200 hover:border-gray-300 cursor-pointer'
                                      } ${isUpdating ? 'opacity-50 cursor-not-allowed' : ''}`}
                                      title={
                                        isOilChangeMonth
                                          ? 'Oil Change Month'
                                          : 'Click to set as Oil Change Month'
                                      }
                                    >
                                      {isOilChangeMonth && <Droplet className="h-3 w-3 mr-1" />}
                                      {monthNames[month - 1]}
                                    </button>
                                  );
                                })}
                              </div>
                            ) : (
                              <span className="text-sm text-gray-400">Not set</span>
                            )}
                          </dd>
                        </div>
                        {fullClient.lastContractServiceDate && (
                          <div>
                            <dt className="text-xs font-medium text-gray-500">Last Service</dt>
                            <dd className="mt-0.5 text-sm text-gray-900">
                              {formatDate(fullClient.lastContractServiceDate)}
                            </dd>
                          </div>
                        )}
                        {fullClient.nextContractServiceDate && (
                          <div>
                            <dt className="text-xs font-medium text-gray-500">Next Service</dt>
                            <dd className="mt-0.5 text-sm text-gray-900">
                              {formatDate(fullClient.nextContractServiceDate)}
                            </dd>
                          </div>
                        )}
                      </dl>
                    </div>
                  )}

                </div>

                {/* Middle Column: Generator & Filters */}
                <div className="space-y-4">
                  {/* Generator Information */}
                  <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-3">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                      <Wrench className="h-4 w-4 mr-2 text-blue-600" />
                      Generator Details
                    </h4>
                    <dl className="space-y-2">
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <dt className="text-xs font-medium text-gray-600">Type</dt>
                          <dd className="mt-0.5 text-sm text-gray-900">{fullClient.generatorType || <span className="text-gray-400">—</span>}</dd>
                        </div>
                        <div>
                          <dt className="text-xs font-medium text-gray-600">Size</dt>
                          <dd className="mt-0.5 text-sm text-gray-900">{fullClient.generatorSize || <span className="text-gray-400">—</span>}</dd>
                        </div>
                      </div>
                      <div>
                        <dt className="text-xs font-medium text-gray-600">Model</dt>
                        <dd className="mt-0.5 text-sm text-gray-900">{fullClient.generatorModel || <span className="text-gray-400">—</span>}</dd>
                      </div>
                      <div>
                        <dt className="text-xs font-medium text-gray-600">Serial Number</dt>
                        <dd className="mt-0.5 text-sm text-gray-900 font-mono">{fullClient.generatorSerialNumber || <span className="text-gray-400">—</span>}</dd>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <dt className="text-xs font-medium text-gray-600">Cooling</dt>
                          <dd className="mt-0.5 text-sm text-gray-900">{formatCoolingType(fullClient.generatorCoolingType) || <span className="text-gray-400">—</span>}</dd>
                        </div>
                        <div>
                          <dt className="text-xs font-medium text-gray-600">Exercise Time</dt>
                          <dd className="mt-0.5 text-sm text-gray-900">{fullClient.generatorExerciseTime || <span className="text-gray-400">—</span>}</dd>
                        </div>
                      </div>
                    </dl>
                  </div>

                  {/* Battery Information */}
                  {(fullClient.batterySize || fullClient.batteryDateCode || fullClient.batteryDateChanged) && (
                    <div className="bg-green-50 border-2 border-green-200 rounded-lg p-3">
                      <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                        <Wrench className="h-4 w-4 mr-2 text-green-600" />
                        Battery Details
                      </h4>
                      <dl className="space-y-2">
                        <div>
                          <dt className="text-xs font-medium text-gray-600">Size</dt>
                          <dd className="mt-0.5 text-sm text-gray-900">{fullClient.batterySize || <span className="text-gray-400">—</span>}</dd>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <dt className="text-xs font-medium text-gray-600">Date Code</dt>
                            <dd className="mt-0.5 text-sm text-gray-900">{fullClient.batteryDateCode || <span className="text-gray-400">—</span>}</dd>
                          </div>
                          <div>
                            <dt className="text-xs font-medium text-gray-600">Date Changed</dt>
                            <dd className="mt-0.5 text-sm text-gray-900">{fullClient.batteryDateChanged ? formatDate(fullClient.batteryDateChanged) : <span className="text-gray-400">—</span>}</dd>
                          </div>
                        </div>
                      </dl>
                    </div>
                  )}

                  {/* Filters & Fluids */}
                  <div className="bg-amber-50 border-2 border-amber-200 rounded-lg p-3">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                      <Droplet className="h-4 w-4 mr-2 text-amber-600" />
                      Filters & Fluids
                    </h4>
                    <dl className="space-y-2">
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <dt className="text-xs font-medium text-gray-600">Air Filter</dt>
                          <dd className="mt-0.5 text-xs text-gray-900">{fullClient.airFilter || <span className="text-gray-400">—</span>}</dd>
                        </div>
                        <div>
                          <dt className="text-xs font-medium text-gray-600">Fuel Filter 1</dt>
                          <dd className="mt-0.5 text-xs text-gray-900">{fullClient.fuelFilter1 || <span className="text-gray-400">—</span>}</dd>
                        </div>
                        <div>
                          <dt className="text-xs font-medium text-gray-600">Fuel Filter 2</dt>
                          <dd className="mt-0.5 text-xs text-gray-900">{fullClient.fuelFilter2 || <span className="text-gray-400">—</span>}</dd>
                        </div>
                        <div>
                          <dt className="text-xs font-medium text-gray-600">Oil Filter 1</dt>
                          <dd className="mt-0.5 text-xs text-gray-900">{fullClient.oilFilter1 || <span className="text-gray-400">—</span>}</dd>
                        </div>
                        <div>
                          <dt className="text-xs font-medium text-gray-600">Oil Filter 2</dt>
                          <dd className="mt-0.5 text-xs text-gray-900">{fullClient.oilFilter2 || <span className="text-gray-400">—</span>}</dd>
                        </div>
                        <div>
                          <dt className="text-xs font-medium text-gray-600">Fuel Additive</dt>
                          <dd className="mt-0.5 text-xs text-gray-900">{fullClient.fuelAdditive || <span className="text-gray-400">—</span>}</dd>
                        </div>
                      </div>
                      <div className="pt-2 border-t border-amber-300">
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <dt className="text-xs font-medium text-gray-600">Fuel Type</dt>
                            <dd className="mt-0.5 text-xs text-gray-900">{fullClient.fuelType || <span className="text-gray-400">—</span>}</dd>
                          </div>
                          <div>
                            <dt className="text-xs font-medium text-gray-600">Oil Type</dt>
                            <dd className="mt-0.5 text-xs text-gray-900">{fullClient.oilType || <span className="text-gray-400">—</span>}</dd>
                          </div>
                        </div>
                        <div className="mt-2">
                          <dt className="text-xs font-medium text-gray-600">Oil Amount</dt>
                          <dd className="mt-0.5 text-xs text-gray-900">{fullClient.oilAmount || <span className="text-gray-400">—</span>}</dd>
                        </div>
                      </div>
                    </dl>
                  </div>

                  {/* Notes */}
                  {fullClient.notes && (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                      <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center">
                        <FileText className="h-4 w-4 mr-2 text-gray-600" />
                        Notes
                      </h4>
                      <p className="text-xs text-gray-700 whitespace-pre-wrap">{fullClient.notes}</p>
                    </div>
                  )}
                </div>

                {/* Right Column: Metadata */}
                <div className="space-y-4">
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">Access & Flags</h4>
                    <div className="space-y-4">
                      <div>
                        <h5 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                          Access Information
                        </h5>
                        <dl className="space-y-2">
                          <div>
                            <dt className="text-xs font-medium text-gray-500">Omni Matrix</dt>
                            <dd className="mt-0.5 text-sm text-gray-900">
                              {fullClient.omniMatrix ? 'Yes' : 'No'}
                            </dd>
                          </div>
                          {fullClient.omniMatrix && (
                            <div>
                              <dt className="text-xs font-medium text-gray-500">Omni Matrix ID</dt>
                              <dd className="mt-0.5 text-sm text-gray-900">
                                {fullClient.omniMatrixId || <span className="text-gray-400">Not set</span>}
                              </dd>
                            </div>
                          )}
                          <div>
                            <dt className="text-xs font-medium text-gray-500">Gate Code Required</dt>
                            <dd className="mt-0.5 text-sm text-gray-900">
                              {fullClient.gateCode ? 'Yes' : 'No'}
                            </dd>
                          </div>
                          {fullClient.gateCode && (
                            <div>
                              <dt className="text-xs font-medium text-gray-500">Gate Code</dt>
                              <dd className="mt-0.5 text-sm text-gray-900 font-mono">
                                {fullClient.gateCodeValue || <span className="text-gray-400">Not set</span>}
                              </dd>
                            </div>
                          )}
                        </dl>
                      </div>
                      <div>
                        <h5 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                          Customer Flags
                        </h5>
                        {fullClient.flags && fullClient.flags.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {fullClient.flags.map((flag) => (
                              <span
                                key={flag.id}
                                className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-xs ${
                                  flag.isActive
                                    ? 'border-blue-200 bg-blue-50 text-blue-700'
                                    : 'border-gray-200 bg-gray-50 text-gray-500'
                                }`}
                              >
                                <FlagIcon
                                  iconName={flag.icon}
                                  className="h-3 w-3"
                                  title={flag.name}
                                />
                                {flag.name}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-400">No flags assigned</p>
                        )}
                      </div>
                      <div>
                        <h5 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                          Technician Rules
                        </h5>
                        {fullClient.techRules && fullClient.techRules.length > 0 ? (
                          <ul className="space-y-1">
                            {fullClient.techRules.map((entry) => {
                              const config = TECH_RULE_DISPLAY[entry.rule] || {};
                              const name = entry.technician
                                ? `${entry.technician.firstName} ${entry.technician.lastName}`
                                : 'Unknown tech';
                              return (
                                <li key={entry.id} className="text-xs">
                                  <span className={`inline-flex items-center rounded-full border px-2 py-0.5 ${config.className || 'border-gray-200 bg-gray-50 text-gray-600'}`}>
                                    {config.label || entry.rule}
                                  </span>
                                  <span className="ml-2 text-gray-900">{name}</span>
                                  {entry.note && (
                                    <span className="ml-1 text-gray-500">— {entry.note}</span>
                                  )}
                                </li>
                              );
                            })}
                          </ul>
                        ) : (
                          <p className="text-sm text-gray-400">Any qualified tech</p>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">System Info</h4>
                    <dl className="space-y-2">
                      <div>
                        <dt className="text-xs font-medium text-gray-500">Created</dt>
                        <dd className="mt-0.5 text-xs text-gray-900">
                          {formatDate(fullClient.createdAt)}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs font-medium text-gray-500">Last Updated</dt>
                        <dd className="mt-0.5 text-xs text-gray-900">
                          {formatDate(fullClient.updatedAt)}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs font-medium text-gray-500">Customer Type</dt>
                        <dd className="mt-0.5 flex gap-2">
                          {fullClient.isContractClient ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                              Contract
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                              Non-Contract
                            </span>
                          )}
                          {fullClient.isCommercial && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                              Commercial
                            </span>
                          )}
                        </dd>
                      </div>
                    </dl>
                  </div>
                </div>
              </div>
            ) : (
              <div>
                {isLoadingHistory || isLoadingHistoryDetails ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                  </div>
                ) : fullServiceCalls.length === 0 ? (
                  <div className="text-center py-12">
                    <Clock className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-4 text-lg font-medium text-gray-900">No Service History</h3>
                    <p className="mt-2 text-sm text-gray-500">
                      This client doesn't have any service calls yet.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {fullServiceCalls
                      .sort((a, b) => {
                        const dateA = new Date(a.completedDate || a.scheduledDate || a.createdAt);
                        const dateB = new Date(b.completedDate || b.scheduledDate || b.createdAt);
                        return dateB - dateA; // Most recent first
                      })
                      .map((serviceCall) => {
                        const displayDate = serviceCall.completedDate || serviceCall.scheduledDate || serviceCall.createdAt;
                        const visits = Array.isArray(serviceCall.visits) ? serviceCall.visits : [];
                        
                        return (
                          <div key={serviceCall.id} className="border border-gray-200 rounded-lg p-4 space-y-3">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <StatusBadge status={serviceCall.status} resolution={serviceCall.resolution} />
                                {serviceCall.isFinalized && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                                    Finalized
                                  </span>
                                )}
                                {serviceCall.type && (
                                  <span className="text-xs text-gray-500">
                                    {serviceCall.type.name}
                                  </span>
                                )}
                                {serviceCall.invoiceNumber && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                                    Inv# {serviceCall.invoiceNumber}
                                  </span>
                                )}
                                {serviceCall.isWarranty && (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                    <ShieldCheck className="h-3 w-3" />
                                    Warranty
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                {displayDate && (
                                  <span className="text-xs text-gray-500">
                                    {formatDate(displayDate)}
                                  </span>
                                )}
                                <button
                                  type="button"
                                  onClick={() => {
                                    setServiceCallToEdit(serviceCall);
                                    setIsEditModalOpen(true);
                                  }}
                                  className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-600 hover:bg-gray-50"
                                >
                                  <Edit className="h-3 w-3" />
                                  Edit
                                </button>
                              </div>
                            </div>

                            {visits.length > 0 ? (
                              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                                <div className="flex items-center justify-between mb-3">
                                  <div className="flex items-center gap-2">
                                    <History className="h-5 w-5 text-yellow-700" />
                                    <h4 className="font-semibold text-gray-900">Visit History ({visits.length} visit{visits.length > 1 ? 's' : ''})</h4>
                                  </div>
                                  {serviceCall.invoiceNumber && (
                                    <span className="text-sm font-medium text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded">
                                      Invoice #{serviceCall.invoiceNumber}
                                    </span>
                                  )}
                                </div>
                                <div className="space-y-3">
                                  {visits.map((visit) => (
                                    <div key={visit.id} className="bg-white rounded-md p-3 border border-yellow-100">
                                      <div className="flex items-center justify-between mb-2">
                                        <span className="font-medium text-gray-900">Visit #{visit.visitNumber}</span>
                                        <span className="text-sm text-gray-600">{formatDate(visit.visitDate)}</span>
                                      </div>
                                      {visit.technician && (
                                        <p className="text-sm text-gray-600 mb-2">
                                          <span className="font-medium">Technician:</span> {visit.technician.firstName} {visit.technician.lastName}
                                        </p>
                                      )}
                                      {visit.type && (
                                        <p className="text-sm text-gray-600 mb-2">
                                          <span className="font-medium">Type:</span> {visit.type.name}
                                        </p>
                                      )}
                                      {visit.workPerformed && (
                                        <div className="mb-2">
                                          <div className="mb-1 flex items-center justify-between gap-2">
                                            <p className="text-sm font-medium text-gray-700">Work Performed:</p>
                                            <CopyTextButton text={visit.workPerformed} size="xs" />
                                          </div>
                                          <p className="text-sm text-gray-700 whitespace-pre-wrap">{visit.workPerformed}</p>
                                        </div>
                                      )}
                                      {visit.outcome === 'needs_return' && visit.reasonForReturn && (
                                        <div className="bg-yellow-50 rounded p-2 mt-2">
                                          <p className="text-sm font-medium text-yellow-900 mb-1">Reason for Return:</p>
                                          <p className="text-sm text-yellow-800">{visit.reasonForReturn}</p>
                                        </div>
                                      )}
                                      {visit.partsUsed && visit.partsUsed.length > 0 && (
                                        <div className="mt-2">
                                          <p className="text-sm font-medium text-gray-700 mb-1">Parts Used:</p>
                                          <ul className="text-sm text-gray-600 list-disc list-inside">
                                            {visit.partsUsed.map((part, partIdx) => (
                                              <li key={partIdx}>
                                                {part.itemNumber || part.name} - Qty: {part.quantity} {part.cost && `($${parseFloat(part.cost).toFixed(2)})`}
                                              </li>
                                            ))}
                                          </ul>
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ) : (
                              <div className="text-sm text-gray-500">No visit history recorded.</div>
                            )}

                            {/* Show root-level parts when visits don't cover them */}
                            {(() => {
                              const rootParts = Array.isArray(serviceCall.partsUsed) ? serviceCall.partsUsed : [];
                              const visitParts = visits.flatMap(v => Array.isArray(v.partsUsed) ? v.partsUsed : []);
                              const visitPartKeys = new Set(visitParts.map(p => `${p.itemNumber || p.name}|${p.quantity}|${p.cost}`));
                              const extraParts = rootParts.filter(p => !visitPartKeys.has(`${p.itemNumber || p.name}|${p.quantity}|${p.cost}`));
                              if (extraParts.length === 0) return null;
                              return (
                                <div className="mt-2">
                                  <p className="text-sm font-medium text-gray-700 mb-1">Parts Used:</p>
                                  <ul className="text-sm text-gray-600 list-disc list-inside">
                                    {extraParts.map((part, partIdx) => (
                                      <li key={partIdx}>
                                        {part.itemNumber || part.name} - Qty: {part.quantity} {part.cost && `($${parseFloat(part.cost).toFixed(2)})`}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              );
                            })()}
                          </div>
                        );
                      })}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-4 py-3 sm:px-6 border-t border-gray-200 sm:flex sm:flex-row-reverse sm:items-center sm:gap-2">
            <button
              type="button"
              onClick={onClose}
              className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:w-auto sm:text-sm"
            >
              Close
            </button>
            {fullClient.isActive && (
              <button
                type="button"
                onClick={handleQueueForRepair}
                disabled={queueForRepairMutation.isLoading}
                className="mt-3 sm:mt-0 w-full inline-flex items-center justify-center gap-1.5 rounded-md border border-amber-300 shadow-sm px-4 py-2 bg-amber-100 text-base font-medium text-amber-800 hover:bg-amber-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 sm:w-auto sm:text-sm disabled:opacity-50"
              >
                {queueForRepairMutation.isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <AlertTriangle className="h-4 w-4" />
                )}
                Queue for Repair
              </button>
            )}
          </div>
        </div>
      </div>
      <ServiceCallEditModal
        serviceCall={serviceCallToEdit}
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setServiceCallToEdit(null);
        }}
      />
    </div>
  );
}








